import { consultRecordingService } from './api/consultRecordingService';
import { scribeJobService, ScribeJob } from './api/scribeJobService';
import { recordingBufferService, RecordingMeta } from './recordingBufferService';

/** One owner above the route: a visit change cannot retarget or cancel an upload. */
export class ScribeUploadQueue {
  private active = new Map<string, Promise<ScribeJob>>();

  private controllers = new Map<string, AbortController>();

  cancelAll() {
    this.controllers.forEach(controller => controller.abort());
    this.controllers.clear();
    this.active.clear();
  }

  submit(meta: RecordingMeta, assertOwner: () => void, onStage: (stage: string) => void) {
    const existing = this.active.get(meta.recordingId);
    if (existing) return existing;
    const controller = new AbortController();
    this.controllers.set(meta.recordingId, controller);
    const guard = () => {
      if (controller.signal.aborted) throw new Error('Upload cancelled after account change.');
      assertOwner();
    };
    const task = this.upload(meta, guard, onStage, controller.signal).finally(() => {
      if (this.controllers.get(meta.recordingId) === controller) {
        this.active.delete(meta.recordingId);
        this.controllers.delete(meta.recordingId);
      }
    });
    this.active.set(meta.recordingId, task);
    return task;
  }

  private async upload(meta: RecordingMeta, assertOwner: () => void, onStage: (stage: string) => void, signal: AbortSignal) {
    assertOwner();
    await recordingBufferService.updateStatus(meta.recordingId, 'uploading');
    onStage('Uploading');
    const blob = await recordingBufferService.assembleRecording(meta.recordingId);
    if (!blob.size) throw new Error('No audio is available to upload.');
    assertOwner();
    const extension = consultRecordingService.getExtensionFromMime(meta.mimeType);
    const allocation = await consultRecordingService.getPresignedUploadUrl(
      meta.sessionId, `${meta.recordingId}.${extension}`, meta.patientId, meta.recordingId, signal,
    );
    assertOwner();
    // Empty URL means this exact recording has already been accepted by the server.
    if (allocation.upload_url) await consultRecordingService.uploadToS3(allocation.upload_url, blob, signal);
    assertOwner();
    onStage('Submitting');
    const job = await scribeJobService.submit(allocation.recording_id, signal);
    assertOwner();
    await recordingBufferService.deleteRecording(meta.recordingId);
    return job;
  }
}
